import { useState, useEffect } from "react";
import { supabase, isSupabaseConfigured } from "@/integrations/supabase/client";

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export function useAuth() {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);


  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('admin_token');
      const userData = localStorage.getItem('admin_user');
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
        } catch (err) {
          console.error('Error parsing user data:', err);
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginCredentials): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check if Supabase is configured
      if (!isSupabaseConfigured()) {
        setError('Database connection not configured. Please check your environment variables.');
        setLoading(false);
        return false;
      }

      // For now, we'll do a simple check against the database
      // In production, you should use proper authentication
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', credentials.email)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        setError('Invalid email or password');
        return false;
      }

      // Simple password check (in production, use proper hashing)
      // For demo purposes, we'll check against the hardcoded password
      if (credentials.password !== 'Aswd@54321') {
        setError('Invalid email or password');
        return false;
      }

      // Update last login
      await supabase
        .from('admin_users')
        .update({ last_login: new Date().toISOString() })
        .eq('id', data.id);

      // Store user data in localStorage
      const userData = {
        id: data.id,
        email: data.email,
        name: data.name,
        is_active: data.is_active,
        last_login: data.last_login,
        created_at: data.created_at,
        updated_at: data.updated_at,
      };

      localStorage.setItem('admin_token', 'demo_token_' + Date.now());
      localStorage.setItem('admin_user', JSON.stringify(userData));
      
      // Set Supabase authentication context for RLS policies
      // We'll use a service role key or create a custom JWT token
      // For now, we'll use the anon key but this should be improved in production
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: 'demo_password_for_rls', // This won't work, but we need to set auth context
      });
      
      if (authError) {
        console.warn('Supabase auth error (expected in demo mode):', authError);
        // In demo mode, we'll continue without proper Supabase auth
        // In production, you should implement proper authentication
      }
      
      // Update state immediately
      setUser(userData);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Login error:', err);
      setError('Login failed. Please try again.');
      setLoading(false);
      return false;
    }
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    
    // Reset state
    setUser(null);
    setLoading(false);
    setError(null);
    
    // Sign out from Supabase if configured
    if (isSupabaseConfigured()) {
      supabase.auth.signOut().catch(console.error);
    }
  };

  const isAuthenticated = () => {
    return user !== null;
  };

  return {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
  };
} 